import { NavLink } from 'react-router-dom';
import { Grid20Filled, Grid20Regular } from '@fluentui/react-icons';
import { MOBILE_NAV, TEAMS_TAB_ROUTES, type NavItem } from '@/components/layout/nav-items';
import { cn } from '@/lib/cn';

/**
 * Bottom navigation for phones (<768px).
 *
 * Every target is at least 44px tall and safe-area aware so it stays usable on
 * the Teams mobile client.
 */

const ADMIN_ENTRY: NavItem = {
  to: '/admin',
  label: 'Administración',
  shortLabel: 'Admin',
  icon: <Grid20Regular />,
  activeIcon: <Grid20Filled />,
  end: true,
  adminOnly: true,
};

export interface MobileNavProps {
  isAdmin: boolean;
  /** Drops the entries Teams already draws as tabs above the content. */
  hideTeamsTabs?: boolean;
  className?: string;
}

export function MobileNav({
  isAdmin,
  hideTeamsTabs = false,
  className,
}: MobileNavProps): JSX.Element | null {
  const base = hideTeamsTabs
    ? MOBILE_NAV.filter((item) => !TEAMS_TAB_ROUTES.has(item.to))
    : MOBILE_NAV;
  const items = isAdmin ? [...base, ADMIN_ENTRY] : base;

  // One destination is not a navigation bar; on the phone that row costs
  // vertical space the calendar needs more.
  if (items.length < 2) return null;

  return (
    <nav
      aria-label="Navegación principal"
      className={cn(
        'z-mobilenav shrink-0 border-t border-edge bg-card md:hidden',
        'pb-[env(safe-area-inset-bottom)]',
        className,
      )}
    >
      <ul className="flex items-stretch justify-around">
        {items.map((item) => (
          <li key={item.to} className="min-w-0 flex-1">
            <NavLink
              to={item.to}
              end={item.end ?? false}
              className={({ isActive }) =>
                cn(
                  'flex h-mobilenav flex-col items-center justify-center gap-0.5 px-1 text-caption font-medium',
                  'transition-colors duration-fast ease-standard',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus',
                  isActive ? 'text-ink-brand' : 'text-ink-muted',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <span
                    className={cn(
                      'grid h-7 w-12 place-items-center rounded-pill transition-colors duration-fast',
                      isActive ? 'bg-brand-50 dark:bg-brand-900/60' : 'bg-transparent',
                    )}
                  >
                    {isActive ? item.activeIcon : item.icon}
                  </span>
                  <span className="w-full truncate text-center">
                    {item.shortLabel ?? item.label}
                  </span>
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
